import { formatNumber } from "@/utils/formatters";
import { getMediaTypeIcon } from "./mediaHelpers";

// Format variant name from attributes
export const formatVariantName = (variant) => {
    // Handle variants with attributeValues (from API)
    if (variant.attributeValues && variant.attributeValues.length > 0) {
        return variant.attributeValues
            .map(attr => `${attr.attribute_name}: ${attr.value}`)
            .join(' / ');
    }
    
    // Handle variants with attributes (from local generation)
    if (variant.attributes && variant.attributes.length > 0) {
        return variant.attributes
            .map(attr => `${attr.name}: ${attr.value}`)
            .join(' / ');
    }
    
    return 'Default Variant';
};

// Format price for display with thousand separators
export const formatPrice = (price) => {
    if (!price) return '';
    return formatNumber(parseFloat(price));
};

// Calculate discount percentage based on prediscount_price and price
export const calculateDiscountPercentage = (prediscountPrice, price) => {
    if (!prediscountPrice || !price || prediscountPrice <= price) {
        return 0;
    }
    
    const discount = ((prediscountPrice - price) / prediscountPrice) * 100;
    return Math.round(discount); // Round to nearest integer
};

// Generate variants from attributes
export const generateVariantsFromAttributes = (attributes) => {
    if (!attributes || attributes.length === 0) return [];
    
    // Helper function to get all combinations
    const generateCombinations = (arrays, current = [], index = 0) => {
        if (index === arrays.length) {
            return [current];
        }
        
        let result = [];
        for (let i = 0; i < arrays[index].length; i++) {
            result = [
                ...result,
                ...generateCombinations(arrays, [...current, arrays[index][i]], index + 1)
            ];
        }
        return result;
    };
    
    // Extract all attribute values, ensuring we handle object values properly
    const attributeValues = attributes.map(attr => {
        return attr.values.map(value => {
            return typeof value === 'object' && value !== null ? value.value : value;
        });
    });
    
    // Generate all combinations
    const combinations = generateCombinations(attributeValues);
    
    // Create variant objects from combinations
    return combinations.map(combination => {
        // Create the attributes field with name/value pairs
        const variantAttributes = attributes.map((attr, index) => ({
            name: attr.name,
            value: combination[index]
        }));
        
        // Create a variant code from the combination (useful for identification)
        const variantCode = variantAttributes
            .map(attr => `${attr.name}-${attr.value}`)
            .join('_');
        
        return {
            id: `variant_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            code: variantCode,
            attributes: variantAttributes,
            price: '',
            prediscount_price: '', // Add prediscount_price field
            mediaId: '', // Main display media
            mediaIds: [], // List of all media IDs for this variant
            isLocked: false,
            isActive: false
        };
    });
};

// Merge existing variants with new ones (to preserve data)
export const mergeVariants = (existingVariants, newVariants) => {
    if (!existingVariants || existingVariants.length === 0) return newVariants;
    
    return newVariants.map(newVar => {
        // Find a matching existing variant by comparing attributes
        const existingVar = existingVariants.find(existVar => {
            if (!existVar.attributes || existVar.attributes.length !== newVar.attributes.length) {
                return false;
            }
            
            // Check if all attributes match
            return existVar.attributes.every((attr, i) => 
                attr.name === newVar.attributes[i].name && 
                attr.value === newVar.attributes[i].value
            );
        });
        
        // If found, preserve existing data, otherwise use the new variant
        return existingVar ? {
            ...newVar,
            id: existingVar.id,
            price: existingVar.price,
            prediscount_price: existingVar.prediscount_price, // Preserve prediscount_price
            mediaId: existingVar.mediaId,
            mediaIds: existingVar.mediaIds || [],
            isLocked: existingVar.isLocked,
            isActive: existingVar.isActive
        } : newVar;
    });
};

// Re-export getMediaTypeIcon from mediaHelpers for convenience
export { getMediaTypeIcon };